import React from 'react';
import { CustomHits } from '../../../components/CustomHits';
import { NoResults } from '../../../components/NoResults';
import { HitCompactView } from '../../../components/Hit/HitCompactView';
import { srpJobCardToHitProps } from '../../../features/srp/hitPropsToSrpJobCard';
import type { ViewMode } from '../hooks/useViewMode';
import type { SrpJobCardViewModel, SrpStaticStrings } from '../../../features/srp/SrpViewModel';

export interface SearchResultsProps {
  viewMode: ViewMode;
  hasNoResults: boolean;
  query?: string;
  className?: string;
  /** When provided with staticViewMode, render from view model (no CustomHits). Same DOM as interactive. */
  staticJobs?: SrpJobCardViewModel[];
  staticViewMode?: ViewMode;
  /** Pre-generated strings for static SRP (no IntlProvider). */
  staticStrings?: SrpStaticStrings;
  /** When true (tools page), show state selector on each card in static shell. */
  isToolsPage?: boolean;
}

/**
 * Search results component - displays CustomHits or NoResults based on state.
 * In static mode (staticJobs + staticViewMode) renders list/grid of HitCompactView for parity.
 */
export const SearchResults: React.FC<SearchResultsProps> = React.memo(({
  viewMode,
  hasNoResults,
  query,
  className = '',
  staticJobs,
  staticViewMode,
  staticStrings,
  isToolsPage = false,
}) => {
  const effectiveViewMode = staticViewMode ?? viewMode;
  const useStatic = staticJobs != null && staticViewMode != null;

  if (hasNoResults && effectiveViewMode !== 'map') {
    return (
      <main id="job-results" className={`job-results ${className}`}>
        {staticStrings ? (
          <section className="srp-results" aria-labelledby="results-title">
            <h2 id="results-title" className="sr-only">{staticStrings.resultsTitle}</h2>
            <p className="text-[#3D3529] dark:text-[#C4A882] py-8">{staticStrings.noResults}</p>
          </section>
        ) : (
          <NoResults query={query} />
        )}
      </main>
    );
  }

  if (useStatic && staticJobs) {
    return (
      <main id="job-results" className={`job-results ${className}`}>
        <section className="srp-results" aria-labelledby="results-title">
          <h2 id="results-title" className="sr-only">
            {staticStrings?.resultsTitle ?? 'Job Search Results'}
          </h2>
          {effectiveViewMode === 'map' ? (
            <div className="w-full sm:px-0 md:px-4 lg:px-4 xl:px-6 2xl:px-8 min-h-[400px] bg-[#F8F5F0] dark:bg-[#170F07] rounded-lg animate-pulse" aria-hidden />
          ) : (
            <ul className="srp-results-grid">
              {staticJobs.map((card, index) => (
                <li
                  key={card.articleId}
                  className="srp-results-grid-item"
                >
                  <HitCompactView
                    {...srpJobCardToHitProps(card)}
                    isToolsPage={isToolsPage}
                    staticHitStrings={staticStrings ? { detailsLabel: staticStrings.hitDetailsLabel, salaryNotSpecified: staticStrings.hitSalaryNotSpecified, salaryInferenceMarketMedian: staticStrings.hitSalaryInferenceMarketMedian, salaryInferenceUnverified: staticStrings.hitSalaryInferenceUnverified, applicationStateSelector: staticStrings.applicationStateSelector, matchDonut: staticStrings.matchDonut, hitCompanyProfileAria: staticStrings.hitCompanyProfileAria, hitCompanyInsightsCue: staticStrings.hitCompanyInsightsCue } : undefined}
                  />
                </li>
              ))}
            </ul>
          )}
        </section>
      </main>
    );
  }

  return (
    <main id="job-results" className={`job-results ${className}`}>
      <CustomHits viewMode={viewMode} />
    </main>
  );
}, (prevProps, nextProps) => {
  return (
    prevProps.viewMode === nextProps.viewMode &&
    prevProps.hasNoResults === nextProps.hasNoResults &&
    prevProps.query === nextProps.query &&
    prevProps.className === nextProps.className &&
    prevProps.staticJobs === nextProps.staticJobs &&
    prevProps.staticViewMode === nextProps.staticViewMode &&
    prevProps.staticStrings === nextProps.staticStrings &&
    prevProps.isToolsPage === nextProps.isToolsPage
  );
});
